import type { MeristemClientConfig } from "./pageDefinitions/types";
import { parseCsvLine, combineWithTerritories } from "./utils/configUtils";

// Note explicitly not loading the timing module here to avoid init overhead
// as well as import cascades.
if (window.performance?.mark) {
  window.performance.mark("bf-exec-start");
}

// eslint-disable-next-line no-underscore-dangle
declare const __GROW_CONFIG__: Record<string, string>;

const injectedConfig = __GROW_CONFIG__;

/**
 * Template/HTML injections
 */

type NodeEnv = "production" | "staging" | "development" | "local" | "test";

const noomEnv = (injectedConfig.NOOM_ENV as NodeEnv) || "local";
const medUSExcludedTerritories = parseCsvLine(
  injectedConfig.MED_US_EXCLUDED_TERRITORIES
);

export const appConfig = {
  NOOM_ENV: noomEnv,

  // Third-party *template* variables
  MIXPANEL_PROJECT_TOKEN: injectedConfig.MIXPANEL_PROJECT_TOKEN,
  ONE_TRUST_DOMAIN_ID: injectedConfig.ONE_TRUST_DOMAIN_ID,
  API_DOMAIN: injectedConfig.API_DOMAIN || origin,
  COOKIE_DOMAIN: injectedConfig.COOKIE_DOMAIN,
  COACH_SERVER_URL: injectedConfig.COACH_SERVER_URL,
  USERPORTAL_URL:
    noomEnv === "production"
      ? "https://account.noom.com/"
      : "https://account.test.wsli.dev/",
  GRAPHQL_URL:
    noomEnv === "production"
      ? "https://graph.noom.com/graphql"
      : "https://graph-test.noom.com/graphql",
  PATIENT_PORTAL_URL:
    noomEnv === "production"
      ? "https://healthcare.noom.com/"
      : "https://health.test.wsli.dev/",
  HEALTH_REPORT_URL:
    noomEnv === "production"
      ? "https://report.noom.com"
      : "https://report.test.wsli.dev",
  REWARDS_API_URL:
    noomEnv === "production"
      ? "https://rewards-api.prod.wsli.dev"
      : "https://rewards-api.test.wsli.dev",
  HEALTHCARE_SERVICE_API_BASE_URL:
    noomEnv === "production"
      ? "https://healthcare-api.noom.com"
      : "https://healthcare-api-test.noom.com",
  BILLING_CORE_API_URL:
    noomEnv === "production"
      ? "https://billing-core.noom.com"
      : "https://billing-core-test.noom.com",
  BILLING_CUSTOMERINFO_API_URL:
    noomEnv === "production"
      ? "https://billing-customerinfo.noom.com"
      : "https://billing-customerinfo.test.wsli.dev",
  OFFERS_API_URL:
    noomEnv === "production"
      ? "https://offers-api.noom.com"
      : "https://offers-api.noomtest.com",
  MARKETING_API_URL:
    noomEnv === "production"
      ? "https://marketing-api.noom.com"
      : "https://marketing-api.noomtest.com",
  IDENTITY_SERVER_URL:
    noomEnv === "production"
      ? "https://auth.noom.com"
      : "https://auth-test.noom.com",
  MARKETING_SERVICE_URL:
    noomEnv === "production"
      ? "https://marketing-api.noom.com"
      : "https://marketing-api.noomtest.com",
  SENTRY_PROJECT_DSN_JS: injectedConfig.SENTRY_PROJECT_DSN_JS,
  BRAINTREE_TOKENIZATION_KEY: injectedConfig.BRAINTREE_TOKENIZATION_KEY,
  STRIPE_API_KEY: injectedConfig.STRIPE_API_KEY,
  GOOGLE_MAPS_API_KEY: injectedConfig.GOOGLE_MAPS_API_KEY,
  RECAPTCHA_SITE_KEY: injectedConfig.RECAPTCHA_SITE_KEY,
  // Fallback to wp domain for localhost, proxied on other domains
  BLOG_DOMAIN: noomEnv === "local" ? "http://webnoomdev.wpengine.com" : "",

  MED_PIXEL_TRACKING_ENABLED:
    injectedConfig.MED_PIXEL_TRACKING_ENABLED === "true",
  ELTV_PNR35_ENABLED: injectedConfig.ELTV_PNR35_ENABLED === "true",
  TELEHEALTH_BUYFLOW_THROTTLE: parseFloat(
    injectedConfig.TELEHEALTH_BUYFLOW_THROTTLE
  ),
  USE_SESSION_STORAGE_FOR_PLAN_TEMPORARY_FLAG:
    injectedConfig.USE_SESSION_STORAGE_FOR_PLAN_TEMPORARY_FLAG === "true",
  REFERRAL_LINK_FLOW_ENABLED:
    injectedConfig.REFERRAL_LINK_FLOW_ENABLED === "true",
  ZUMBA_OFFER_ENABLED: injectedConfig.ZUMBA_OFFER_ENABLED === "true",
  NMC_PRODUCT_VARIANT_ID: injectedConfig.NMC_PRODUCT_VARIANT_ID,
  SINGULAR_SDK_KEY: injectedConfig.SINGULAR_SDK_KEY,
  SINGULAR_SDK_SECRET: injectedConfig.SINGULAR_SDK_SECRET,
  SINGULAR_SDK_PRODUCT_ID: injectedConfig.SINGULAR_SDK_PRODUCT_ID,
  MEAL_PLAN_ALLERGY_QUESTION_ENABLED:
    injectedConfig.MEAL_PLAN_ALLERGY_QUESTION_ENABLED === "true",
  MEAL_PLAN_FAVORITE_CUISINE_QUESTION_ENABLED:
    injectedConfig.MEAL_PLAN_FAVORITE_CUISINE_QUESTION_ENABLED === "true",
  MEAL_PLAN_MAX_COOKING_TIME_QUESTION_ENABLED:
    injectedConfig.MEAL_PLAN_MAX_COOKING_TIME_QUESTION_ENABLED === "true",
  INTL_NYE_HOLIDAY_PROMO_BANNER_START_TIME:
    injectedConfig.INTL_NYE_HOLIDAY_PROMO_BANNER_START_TIME,
  INTL_NYE_HOLIDAY_PROMO_BANNER_END_TIME:
    injectedConfig.INTL_NYE_HOLIDAY_PROMO_BANNER_END_TIME,
  STANDALONE_HRT_BUYFLOW_ENABLED:
    injectedConfig.STANDALONE_HRT_BUYFLOW_ENABLED === "true",
  HEALTH_ROUTE_ENABLED: injectedConfig.HEALTH_ROUTE_ENABLED === "true",
  MED_COMPOUNDED_503B_TO_503A_PHARMACY_TRANSITION:
    injectedConfig.MED_COMPOUNDED_503B_TO_503A_PHARMACY_TRANSITION === "true",
  MED_IS_ADDRESS_CHANGE_ENABLED:
    injectedConfig.MED_IS_ADDRESS_CHANGE_ENABLED === "true",
  LONGEVITY_MICRODOSE_ENABLED:
    injectedConfig.LONGEVITY_MICRODOSE_ENABLED === "true",
  LONGEVITY_MICRODOSE_TRAFFIC_SPLIT: parseFloat(
    injectedConfig.LONGEVITY_MICRODOSE_TRAFFIC_SPLIT
  ),
  LONGEVITY_MICRODOSE_HOLDBACK_TRAFFIC: parseFloat(
    injectedConfig.LONGEVITY_MICRODOSE_HOLDBACK_TRAFFIC
  ),
  OPT_OUT_STATES: parseCsvLine(
    injectedConfig.OPT_OUT_STATES,
    "CA,CO,CT,DE,IA,MT,MN,NE,NH,NJ,NV,OR,TN,TX,UT,VA,WA"
  ),
  INSURANCE_CHECKER_ROUTE_ENABLED:
    injectedConfig.INSURANCE_CHECKER_ROUTE_ENABLED === "true",
  HEALTH_INSURANCE_MEDICARE_OPTION_ENABLED:
    injectedConfig.HEALTH_INSURANCE_MEDICARE_OPTION_ENABLED === "true",
  PRODUCT_SWITCH_ENABLED: injectedConfig.PRODUCT_SWITCH_ENABLED === "true",
  SEQUENTIAL_PATIENT_INTAKES_ENABLED:
    injectedConfig.SEQUENTIAL_PATIENT_INTAKES_ENABLED === "true",
  // State Eligibility Configuration
  // All medical plan states combined with territories
  COMPOUNDED_PLAN_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.COMPOUNDED_PLAN_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),
  LIRAGLUTIDE_PLAN_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.LIRAGLUTIDE_PLAN_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),
  BRANDED_PLAN_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.BRANDED_PLAN_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),
  COMPOUNDED_MICRODOSE_PLAN_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.COMPOUNDED_MICRODOSE_PLAN_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),
  LONGEVITY_MICRODOSE_PLAN_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.LONGEVITY_MICRODOSE_PLAN_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),
  HRT_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.HRT_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),
  ORALS_PLAN_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.ORALS_PLAN_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),
  GLP1_INCREASED_BMI_US_STATES: parseCsvLine(
    injectedConfig.GLP1_INCREASED_BMI_US_STATES
  ),

  TIRZEPATIDE_PLAN_ENABLED: injectedConfig.TIRZEPATIDE_PLAN_ENABLED === "true",
  TIRZEPATIDE_PLAN_INELIGIBLE_US_STATES: combineWithTerritories(
    parseCsvLine(injectedConfig.TIRZEPATIDE_PLAN_INELIGIBLE_US_STATES),
    medUSExcludedTerritories
  ),

  FUTURE_ME_ENABLED: injectedConfig.FUTURE_ME_ENABLED === "true",
  CONTINUITY_OF_CARE_ENABLED:
    injectedConfig.CONTINUITY_OF_CARE_ENABLED === "true",
  APPLE_PURCHASE_LINKOUT_ENABLED:
    injectedConfig.APPLE_PURCHASE_LINKOUT_DISABLED === "false",
  LOGGED_OUT_BUYFLOW_CLIENT_TOKEN:
    injectedConfig.LOGGED_OUT_BUYFLOW_CLIENT_TOKEN,
  PROFILE_IMAGE_S3_BUCKET_NAME:
    noomEnv === "production"
      ? "noom-coach-mobile-prod"
      : "noom-coach-mobile-test",

  COUNTER_OFFER_INSTALLMENTS_ELIGIBLE_PRODUCT_NAMES: parseCsvLine(
    injectedConfig.COUNTER_OFFER_INSTALLMENTS_ELIGIBLE_PRODUCT_NAMES
  ),

  meristemConfig: (injectedConfig.meristemConfig || {}) as MeristemClientConfig,
} as const;
